import { JaneGoldUserCallout } from '@iheartjane/menu';
import { Box, Flex } from '@iheartjane/reefer';
import { EventNames } from '@iheartjane/tracking';
import { formatCurrency } from '@iheartjane/utils';
import { useCallback, useState } from 'react';

import { useJaneGoldData } from '@jane/shared-ecomm/hooks';
import { linkBankAccountEvent } from '@jane/shared-ecomm/tracking';
import type { CalloutVariants } from '@jane/shared-ecomm/types';
import { postMessageToIframeParent } from '@jane/shared/util';

import { BankLinkingModal } from '../bankLinkingModal/bankLinkingModal';
import { LogInModal } from '../logInModal/logInModal';
import styles from './janeGoldCallout.module.css';
import type { JaneGoldCalloutProps } from './janeGoldCallout.types';
import { JaneGoldCalloutDescription } from './janeGoldCalloutDescription/janeGoldCalloutDescription';
import { JaneGoldCartCallout } from './janeGoldCartCallout/janeGoldCartCallout';
import { JaneGoldCheckoutCallout } from './janeGoldCheckoutCallout/janeGoldCheckoutCallout';
import { JaneGoldCheckoutSummaryCallout } from './janeGoldCheckoutSummaryCallout/janeGoldCheckoutSummaryCallout';
import { JaneGoldMenuPdpCallout } from './janeGoldMenuPdpCallout/janeGoldMenuPdpCallout';
import { JaneGoldPdpCallout } from './janeGoldPdpCallout/janeGoldPdpCallout';

/**
 * A component used for representing Jane Gold callout action. This callout has three different variants,
 * depending on the context where it is used. Different variants render different content and calls to action, depending on if the user has a linked bank or not.
 */

export function JaneGoldCallout({
  authentication,
  brandSpecials = [],
  bloomUserExperience,
  bundlesInProgress,
  cartBrandDiscounts,
  cartUuid,
  defaultOpen = false,
  discountLabel,
  externalAuth = false,
  feeLabel,
  headlessLoginUrl,
  hideJanePayBranding,
  onBrandSpecialClick,
  onLinkAccountClick,
  onMenu,
  segmentedDeal = false,
  storeId,
  storeState,
  totalLabel,
  variant = 'pdp',
  ...props
}: JaneGoldCalloutProps) {
  const { isGuestUser, userData, userHasLinkedBank } = useJaneGoldData();
  const [openModal, setOpenModal] = useState(defaultOpen);

  const pdpCalloutTitle = `${discountLabel}`;
  const checkoutCalloutTitle = userHasLinkedBank
    ? `${formatCurrency(discountLabel)} cash back earned with Jane Gold`
    : `Earn ${formatCurrency(discountLabel)} cash back with Jane Gold`;
  const cartCalloutTitle = 'Jane Gold rewards';

  const buttonLabel =
    isGuestUser && variant !== 'checkout'
      ? `Sign in${segmentedDeal && variant === 'pdp' ? ' for eligibility' : ''}`
      : 'Link bank account';

  const hasFeeBreakdown = Boolean(feeLabel && Number(feeLabel) > 0);

  const handleOnLinkAccountClick = useCallback(() => {
    linkBankAccountEvent({
      email: userData?.user.email || '',
      event: EventNames.LinkBankAccountTap,
      source: variant,
      username: userData?.user.nickname || '',
    });
    onLinkAccountClick && onLinkAccountClick();

    if (isGuestUser && externalAuth && bloomUserExperience) {
      postMessageToIframeParent({
        messageType: 'authEvent',
        payload: {
          name: 'initiateExternalAuth',
          source: 'bag',
        },
      });
    } else {
      setOpenModal(true);
    }
  }, [userData, variant, externalAuth, isGuestUser, onLinkAccountClick]);

  return (
    <Box {...props} className={styles['janeGoldCallout']}>
      {variant === 'pdp' && (
        <JaneGoldPdpCallout
          buttonLabel={buttonLabel}
          description={
            <CalloutDescription
              isGuestUser={isGuestUser}
              userHasLinkedBank={userHasLinkedBank}
              variant={variant}
            />
          }
          onLinkAccountClick={handleOnLinkAccountClick}
          onMenu={onMenu}
          title={pdpCalloutTitle}
        />
      )}
      {variant === 'menuPdp' && (
        <Flex flexDirection="column" gap={16}>
          {brandSpecials.map((brandSpecial) => (
            <JaneGoldMenuPdpCallout
              key={brandSpecial.id}
              brandSpecial={brandSpecial}
              cartBrandDiscounts={cartBrandDiscounts}
              cartUuid={cartUuid as string}
              isGuestUser={isGuestUser}
              onBrandSpecialClick={onBrandSpecialClick}
            />
          ))}
        </Flex>
      )}
      {variant === 'cart' && (
        <JaneGoldCartCallout
          bundlesInProgress={bundlesInProgress}
          buttonLabel={buttonLabel}
          cartBrandDiscounts={cartBrandDiscounts}
          cartUuid={cartUuid}
          description={
            <CalloutDescription
              isGuestUser={isGuestUser}
              userHasLinkedBank={userHasLinkedBank}
              variant={variant}
            />
          }
          discountLabel={discountLabel}
          feeLabel={feeLabel}
          hasBreakdown={hasFeeBreakdown}
          headlessLoginUrl={headlessLoginUrl}
          onBrandSpecialClick={onBrandSpecialClick}
          onLinkAccountClick={handleOnLinkAccountClick}
          storeId={storeId}
          totalLabel={totalLabel}
          title={cartCalloutTitle}
        />
      )}
      {variant === 'checkout' && (
        <JaneGoldCheckoutCallout
          cartBrandDiscounts={cartBrandDiscounts}
          buttonLabel={buttonLabel}
          description={
            <CalloutDescription
              isGuestUser={isGuestUser}
              userHasLinkedBank={userHasLinkedBank}
              variant={variant}
            />
          }
          discountLabel={discountLabel}
          feeLabel={feeLabel}
          hasBreakdown={hasFeeBreakdown}
          onLinkAccountClick={handleOnLinkAccountClick}
          title={checkoutCalloutTitle}
          totalLabel={totalLabel}
        />
      )}
      {variant === 'checkoutSummary' && (
        <JaneGoldCheckoutSummaryCallout
          discountLabel={discountLabel}
          onLinkAccountClick={handleOnLinkAccountClick}
        />
      )}
      {variant === 'goldRewards' && (
        <JaneGoldUserCallout
          onLinkAccountClick={handleOnLinkAccountClick}
          isSegmentedDeal={segmentedDeal}
          storeState={storeState}
          userSignedIn={!isGuestUser}
          userHasLinkedBank={userHasLinkedBank}
        />
      )}

      {openModal && !isGuestUser && !userHasLinkedBank && (
        <BankLinkingModal
          open={openModal}
          onRequestClose={() => setOpenModal(false)}
          location={variant}
          hideJanePayBranding={hideJanePayBranding}
          isGold
        />
      )}

      {openModal && authentication && isGuestUser && (
        <LogInModal
          errors={authentication.errors}
          location={variant}
          open={true}
          onAppleClick={authentication.onAppleClick}
          onGoogleClick={authentication.onGoogleClick}
          onRequestClose={() => {
            authentication.resetError();
            setOpenModal(false);
          }}
          onSignInSubmit={authentication.login}
          onCreateAccountSubmit={authentication.register}
          onForgotPasswordSubmit={authentication.resetPassword}
          showAppleLogin={authentication.showAppleLogin}
          showGoogleLogin={authentication.showGoogleLogin}
        />
      )}
    </Box>
  );
}

const CalloutDescription = ({
  isGuestUser,
  userHasLinkedBank,
  variant,
}: {
  isGuestUser: boolean;
  userHasLinkedBank: boolean;
  variant: NonNullable<CalloutVariants>;
}) => {
  return (
    <JaneGoldCalloutDescription
      isGuestUser={isGuestUser}
      userHasLinkedBank={userHasLinkedBank}
      variant={variant}
    />
  );
};
